import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  DollarSign,
  TrendingDown,
  TrendingUp,
  Banknote,
  CreditCard,
  ArrowLeftRight,
  Wallet,
  Users,
  Package,
  Receipt,
  Plus,
  BookOpen,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const EXPENSE_CATEGORIES = [
  { value: 'renta', label: 'Renta' },
  { value: 'servicios', label: 'Servicios' },
  { value: 'mantenimiento', label: 'Mantenimiento' },
  { value: 'insumos', label: 'Insumos' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'general', label: 'General' },
];

const fmtMoney = (v) =>
  `$${(v || 0).toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function AdminBalance() {
  const { user, getAuthHeaders } = useAuth();
  const now = new Date();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [balance, setBalance] = useState(null);
  const [salesSummary, setSalesSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: '',
    category: 'general',
  });
  const [saving, setSaving] = useState(false);

  const isMasterAdmin = user?.role === 'master_admin';

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [y, y - 1, y - 2];
  }, [now]);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchBalance();
  }, [selectedBranch, selectedMonth, selectedYear]);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/branches`, getAuthHeaders());
      let available = res.data;
      if ((user?.role === 'manager' || user?.role === 'admin') && user?.branch_id) {
        available = res.data.filter((b) => b.id === user.branch_id);
        setSelectedBranch(user.branch_id);
      }
      setBranches(available);
    } catch (e) {
      // silent
    }
  };

  const monthRange = () => {
    const mm = String(selectedMonth).padStart(2, '0');
    const startDate = `${selectedYear}-${mm}-01`;
    const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
    const endDate = `${selectedYear}-${mm}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate, monthStr: `${selectedYear}-${mm}` };
  };

  const fetchBalance = async () => {
    setLoading(true);
    try {
      const { startDate, endDate, monthStr } = monthRange();
      const branchQ =
        selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : '';
      const branchQ2 =
        selectedBranch !== 'all' ? `?branch_id=${selectedBranch}` : '';

      const [balRes, sumRes] = await Promise.all([
        axios.get(
          `${API}/reports/monthly-balance${branchQ2}${branchQ2 ? '&' : '?'}month=${monthStr}`,
          getAuthHeaders()
        ),
        axios.get(
          `${API}/reports/sales-summary?start_date=${startDate}&end_date=${endDate}${branchQ}`,
          getAuthHeaders()
        ),
      ]);
      setBalance(balRes.data);
      setSalesSummary(sumRes.data);
    } catch (e) {
      toast.error('Error al cargar balance');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExpense = async () => {
    const amount = parseFloat(expenseForm.amount);
    if (!expenseForm.description.trim()) {
      toast.error('Ingresa una descripción');
      return;
    }
    if (isNaN(amount) || amount <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }
    setSaving(true);
    try {
      const params = new URLSearchParams({
        description: expenseForm.description.trim(),
        amount: String(amount),
        category: expenseForm.category,
      });
      await axios.post(
        `${API}/expenses?${params.toString()}`,
        {},
        getAuthHeaders()
      );
      toast.success('Gasto registrado');
      setExpenseForm({ description: '', amount: '', category: 'general' });
      setExpenseOpen(false);
      fetchBalance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al registrar gasto');
    } finally {
      setSaving(false);
    }
  };

  const income = balance?.income || {};
  const expenses = balance?.expenses || {};
  const byPayment = income.by_payment_method || {};
  const totalIncome = income.total || 0;
  const totalExpenses = expenses.total || 0;
  const netMargin = totalIncome - totalExpenses;
  const marginPct = totalIncome > 0 ? (netMargin / totalIncome) * 100 : 0;
  const isPositive = netMargin >= 0;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-balance-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-gold" />
              <h1 className="font-heading text-3xl text-white tracking-tight">
                Balance General
              </h1>
            </div>
            <p className="text-zinc-500 mt-1 text-sm">
              Reporte financiero mensual · Ingresos, egresos y margen neto
            </p>
          </div>
        </div>

        {/* Filter bar */}
        <Card
          className="bg-zinc-900/50 border-zinc-800 p-4"
          data-testid="balance-filter-bar"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex flex-wrap gap-3">
              {isMasterAdmin && (
                <Select
                  value={selectedBranch}
                  onValueChange={setSelectedBranch}
                >
                  <SelectTrigger
                    className="w-[200px] bg-zinc-950 border-zinc-800 text-white"
                    data-testid="balance-branch-select"
                  >
                    <SelectValue placeholder="Sucursal" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="all" className="text-white">
                      Todas las sucursales
                    </SelectItem>
                    {branches.map((b) => (
                      <SelectItem
                        key={b.id}
                        value={b.id}
                        className="text-white"
                      >
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select
                value={String(selectedMonth)}
                onValueChange={(v) => setSelectedMonth(parseInt(v))}
              >
                <SelectTrigger
                  className="w-[160px] bg-zinc-950 border-zinc-800 text-white"
                  data-testid="balance-month-select"
                >
                  <SelectValue placeholder="Mes" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {MONTHS_ES.map((m, idx) => (
                    <SelectItem
                      key={idx}
                      value={String(idx + 1)}
                      className="text-white"
                    >
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={String(selectedYear)}
                onValueChange={(v) => setSelectedYear(parseInt(v))}
              >
                <SelectTrigger
                  className="w-[120px] bg-zinc-950 border-zinc-800 text-white"
                  data-testid="balance-year-select"
                >
                  <SelectValue placeholder="Año" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {yearOptions.map((y) => (
                    <SelectItem
                      key={y}
                      value={String(y)}
                      className="text-white"
                    >
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={() => setExpenseOpen(true)}
              className="bg-gold text-black hover:bg-gold/90"
              data-testid="balance-add-expense-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar Gasto
            </Button>
          </div>
        </Card>

        {loading ? (
          <div className="text-center py-16 text-zinc-500">Cargando...</div>
        ) : (
          <>
            {/* 3 Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card
                className="bg-zinc-900/50 border-zinc-800 p-5"
                data-testid="balance-total-sales-card"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">
                      Total Ventas
                    </p>
                    <p className="font-heading text-3xl text-green-500">
                      {fmtMoney(totalIncome)}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {income.transactions_count || 0} transacciones
                    </p>
                  </div>
                  <div className="w-11 h-11 rounded-sm bg-green-500/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                  </div>
                </div>
              </Card>

              <Card
                className="bg-zinc-900/50 border-zinc-800 p-5"
                data-testid="balance-total-expenses-card"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">
                      Total Egresos
                    </p>
                    <p className="font-heading text-3xl text-red-500">
                      {fmtMoney(totalExpenses)}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Comisiones + Gastos + Costos
                    </p>
                  </div>
                  <div className="w-11 h-11 rounded-sm bg-red-500/10 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-500" />
                  </div>
                </div>
              </Card>

              <Card
                className={`p-5 border ${
                  isPositive
                    ? 'bg-gold/5 border-gold/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}
                data-testid="balance-net-margin-card"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">
                      Margen Neto
                    </p>
                    <p
                      className={`font-heading text-3xl ${
                        isPositive ? 'text-gold' : 'text-red-500'
                      }`}
                    >
                      {fmtMoney(netMargin)}
                    </p>
                    <p
                      className={`text-xs mt-1 font-semibold ${
                        isPositive ? 'text-gold' : 'text-red-500'
                      }`}
                    >
                      {marginPct.toFixed(1)}% de margen
                    </p>
                  </div>
                  <div
                    className={`w-11 h-11 rounded-sm flex items-center justify-center ${
                      isPositive ? 'bg-gold/10' : 'bg-red-500/10'
                    }`}
                  >
                    <Wallet
                      className={`w-5 h-5 ${
                        isPositive ? 'text-gold' : 'text-red-500'
                      }`}
                    />
                  </div>
                </div>
              </Card>
            </div>

            {/* Income breakdown card */}
            <Card
              className="bg-zinc-900/50 border-zinc-800 p-6"
              data-testid="balance-income-breakdown-card"
            >
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-4 h-4 text-gold" />
                <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                  Desglose de Ingresos
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-4 rounded-sm bg-zinc-950/60 border border-zinc-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Banknote className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs text-zinc-500 uppercase tracking-widest">
                      Efectivo
                    </span>
                  </div>
                  <p className="font-heading text-2xl text-white">
                    {fmtMoney(byPayment.cash)}
                  </p>
                </div>
                <div className="p-4 rounded-sm bg-zinc-950/60 border border-zinc-800">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-sky-400" />
                    <span className="text-xs text-zinc-500 uppercase tracking-widest">
                      Tarjeta
                    </span>
                  </div>
                  <p className="font-heading text-2xl text-white">
                    {fmtMoney(byPayment.card)}
                  </p>
                </div>
                <div className="p-4 rounded-sm bg-zinc-950/60 border border-zinc-800">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowLeftRight className="w-4 h-4 text-violet-400" />
                    <span className="text-xs text-zinc-500 uppercase tracking-widest">
                      Transferencia
                    </span>
                  </div>
                  <p className="font-heading text-2xl text-white">
                    {fmtMoney(byPayment.transfer)}
                  </p>
                </div>
              </div>
              {byPayment.combined > 0 && (
                <p className="text-xs text-zinc-500 mt-3">
                  * Pagos combinados: {fmtMoney(byPayment.combined)} (repartidos
                  entre efectivo y tarjeta según el ticket)
                </p>
              )}
            </Card>

            {/* Expense cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card
                className="bg-zinc-900/50 border-zinc-800 p-5"
                data-testid="balance-payroll-card"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-gold" />
                  <h3 className="text-xs text-zinc-500 uppercase tracking-widest">
                    Nóminas / Comisiones
                  </h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">Pendientes</span>
                    <span className="text-white font-semibold">
                      {fmtMoney(expenses.commissions_pending)}
                    </span>
                  </div>
                  <div className="pl-3 border-l border-zinc-800 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">• Servicios</span>
                      <span className="text-zinc-400">
                        {fmtMoney(expenses.commissions_services)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">• Propinas</span>
                      <span className="text-zinc-400">
                        {fmtMoney(expenses.commissions_tips)}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-zinc-800">
                    <span className="text-sm text-zinc-400">Pagadas</span>
                    <span className="text-emerald-400 font-semibold">
                      {fmtMoney(expenses.commissions_paid)}
                    </span>
                  </div>
                </div>
              </Card>

              <Card
                className="bg-zinc-900/50 border-zinc-800 p-5"
                data-testid="balance-fixed-expenses-card"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Receipt className="w-4 h-4 text-gold" />
                  <h3 className="text-xs text-zinc-500 uppercase tracking-widest">
                    Gastos Fijos
                  </h3>
                </div>
                <p className="font-heading text-3xl text-white">
                  {fmtMoney(expenses.fixed_expenses)}
                </p>
                <p className="text-xs text-zinc-500 mt-2">
                  Renta, servicios y recurrentes del mes
                </p>
                <div className="mt-3 pt-3 border-t border-zinc-800 flex justify-between text-xs">
                  <span className="text-zinc-500">Otros gastos</span>
                  <span className="text-zinc-300">
                    {fmtMoney(expenses.other)}
                  </span>
                </div>
              </Card>

              <Card
                className="bg-zinc-900/50 border-zinc-800 p-5"
                data-testid="balance-product-costs-card"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-gold" />
                  <h3 className="text-xs text-zinc-500 uppercase tracking-widest">
                    Costos de Inventario
                  </h3>
                </div>
                <p className="font-heading text-3xl text-white">
                  {fmtMoney(expenses.product_costs)}
                </p>
                <p className="text-xs text-zinc-500 mt-2">
                  Costo de productos vendidos / repuestos
                </p>
              </Card>
            </div>

            {/* Monthly summary table */}
            <Card
              className="bg-zinc-900/50 border-zinc-800 p-6"
              data-testid="balance-summary-table-card"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                  Resumen del mes ·{' '}
                  <span className="text-gold">
                    {balance?.month_name || `${MONTHS_ES[selectedMonth - 1]} ${selectedYear}`}
                  </span>
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm"
                  data-testid="balance-summary-table"
                >
                  <thead>
                    <tr className="border-b border-zinc-800 text-left text-zinc-500 uppercase text-xs tracking-widest">
                      <th className="py-3 px-2">Concepto</th>
                      <th className="py-3 px-2 text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/70">
                    <tr className="bg-green-500/5">
                      <td className="py-2.5 px-2 text-emerald-400 font-medium uppercase text-xs tracking-widest">
                        Ingresos
                      </td>
                      <td className="py-2.5 px-2 text-right text-emerald-400 font-semibold">
                        {fmtMoney(totalIncome)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 px-2 pl-6 text-zinc-400">
                        · Servicios
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-300">
                        {fmtMoney(income.services)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 px-2 pl-6 text-zinc-400">
                        · Productos
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-300">
                        {fmtMoney(income.products)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 px-2 pl-6 text-zinc-400">
                        · Efectivo
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-300">
                        {fmtMoney(byPayment.cash)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 px-2 pl-6 text-zinc-400">
                        · Tarjeta
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-300">
                        {fmtMoney(byPayment.card)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 px-2 pl-6 text-zinc-400">
                        · Transferencia
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-300">
                        {fmtMoney(byPayment.transfer)}
                      </td>
                    </tr>

                    <tr className="bg-red-500/5">
                      <td className="py-2.5 px-2 text-red-400 font-medium uppercase text-xs tracking-widest">
                        Egresos
                      </td>
                      <td className="py-2.5 px-2 text-right text-red-400 font-semibold">
                        {fmtMoney(totalExpenses)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 px-2 pl-6 text-zinc-400">
                        · Comisiones pendientes (servicios + propinas)
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-300">
                        {fmtMoney(expenses.commissions_pending)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 px-2 pl-6 text-zinc-400">
                        · Comisiones pagadas
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-300">
                        {fmtMoney(expenses.commissions_paid)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 px-2 pl-6 text-zinc-400">
                        · Gastos fijos
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-300">
                        {fmtMoney(expenses.fixed_expenses)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 px-2 pl-6 text-zinc-400">
                        · Costos de inventario
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-300">
                        {fmtMoney(expenses.product_costs)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 px-2 pl-6 text-zinc-400">
                        · Otros gastos
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-300">
                        {fmtMoney(expenses.other)}
                      </td>
                    </tr>

                    <tr
                      className={`border-t-2 ${
                        isPositive
                          ? 'border-gold/40 bg-gold/5'
                          : 'border-red-500/40 bg-red-500/10'
                      }`}
                    >
                      <td
                        className={`py-4 px-2 font-bold uppercase tracking-widest ${
                          isPositive ? 'text-gold' : 'text-red-400'
                        }`}
                        data-testid="balance-net-margin-label"
                      >
                        Margen Neto
                      </td>
                      <td
                        className={`py-4 px-2 text-right font-bold text-lg ${
                          isPositive ? 'text-gold' : 'text-red-400'
                        }`}
                        data-testid="balance-net-margin-value"
                      >
                        {fmtMoney(netMargin)}{' '}
                        <span className="text-xs font-normal opacity-70">
                          ({marginPct.toFixed(1)}%)
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Add expense dialog */}
      <Dialog open={expenseOpen} onOpenChange={setExpenseOpen}>
        <DialogContent
          className="bg-zinc-900 border-zinc-800 text-white"
          data-testid="balance-expense-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-gold">Agregar Gasto</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Se registrará como egreso del mes actual.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-zinc-400" htmlFor="expense-description">
                Descripción
              </Label>
              <Input
                id="expense-description"
                value={expenseForm.description}
                onChange={(e) =>
                  setExpenseForm({
                    ...expenseForm,
                    description: e.target.value,
                  })
                }
                placeholder="Ej. Pago de renta local"
                className="bg-zinc-950 border-zinc-800 text-white"
                data-testid="balance-expense-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400" htmlFor="expense-amount">
                Monto
              </Label>
              <Input
                id="expense-amount"
                type="number"
                min="0"
                step="0.01"
                value={expenseForm.amount}
                onChange={(e) =>
                  setExpenseForm({ ...expenseForm, amount: e.target.value })
                }
                placeholder="0.00"
                className="bg-zinc-950 border-zinc-800 text-white"
                data-testid="balance-expense-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Categoría</Label>
              <Select
                value={expenseForm.category}
                onValueChange={(v) =>
                  setExpenseForm({ ...expenseForm, category: v })
                }
              >
                <SelectTrigger
                  className="bg-zinc-950 border-zinc-800 text-white"
                  data-testid="balance-expense-category-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {EXPENSE_CATEGORIES.map((c) => (
                    <SelectItem
                      key={c.value}
                      value={c.value}
                      className="text-white"
                    >
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setExpenseOpen(false)}
              className="bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-white"
              data-testid="balance-expense-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateExpense}
              disabled={saving}
              className="bg-gold text-black hover:bg-gold/90"
              data-testid="balance-expense-save-btn"
            >
              {saving ? 'Guardando...' : 'Guardar Gasto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
